import { t as uiText } from '../i18n';
import React from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import { useAuthContext } from '../context/AuthContext';
import { useWorkspaceContext } from '../context/WorkspaceContext';

type PlanId = 'free' | 'starter' | 'standard' | 'team';

type WorkspaceBillingInfo = {
  name?: string;
  planId?: string;
  parentWorkspaceId?: string;
};

type CreateCheckoutOrUpdateSubscriptionResponse =
  | { kind: 'checkout'; url: string }
  | { kind: 'scheduled' | 'cancelled' | 'updated'; message?: string };

function normalizePlanId(input: unknown): PlanId {
  const raw = typeof input === 'string' ? input.trim().toLowerCase() : '';
  if (raw === 'starter' || raw === 'standard' || raw === 'team') return raw;
  return 'free';
}

function openInNewTab(path: string) {
  const url = `${window.location.origin}${path}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

export default function PlanChangeFlowModal(props: {
  isOpen: boolean;
  workspaceId: string;
  targetPlanId: PlanId;
  onClose: () => void;
}) {
  const { isOpen, workspaceId, targetPlanId, onClose } = props;
  const { user } = useAuthContext();
  const { refresh } = useWorkspaceContext();

  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [workspaceInfo, setWorkspaceInfo] = React.useState<WorkspaceBillingInfo | null>(null);
  const currentPlanId = normalizePlanId(workspaceInfo?.planId);

  const isChildWorkspace = !!workspaceInfo?.parentWorkspaceId;

  const mode = import.meta.env.MODE === 'production' ? 'live' : 'test';

  React.useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const snap = await getDoc(doc(db, 'workspaces', workspaceId));
        if (!snap.exists()) {
          throw new Error(uiText("Workspace not found"));
        }
        const data = snap.data() as any;
        const info: WorkspaceBillingInfo = {
          name: typeof data?.name === 'string' ? data.name : undefined,
          planId: typeof data?.planId === 'string' ? data.planId : undefined,
          parentWorkspaceId: typeof data?.parentWorkspaceId === 'string' ? data.parentWorkspaceId : undefined,
        };
        if (!cancelled) setWorkspaceInfo(info);
      } catch (e: any) {
        console.error('[PlanChangeFlowModal] load workspace failed', e);
        if (!cancelled) setError(e?.message || uiText("Couldn't load"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [isOpen, workspaceId]);

  if (!isOpen) return null;

  const canProceed = !!user?.uid && !submitting && !loading && currentPlanId !== targetPlanId;

  const submit = async () => {
    setError(null);

    if (!user?.uid) {
      setError(uiText("Sign in to do this."));
      return;
    }

    if (isChildWorkspace) return;

    try {
      setSubmitting(true);
      const fn = httpsCallable(functions, 'createCheckoutOrUpdateSubscriptionFn');

      const res = await fn({
        workspaceId,
        targetPlanId,
        mode,
        returnUrl: `${window.location.origin}/${workspaceId}`,
      });

      const data = res.data as any as CreateCheckoutOrUpdateSubscriptionResponse;
      if (data?.kind === 'checkout' && data?.url) {
        window.location.assign(data.url);
        return;
      }

      const message =
        (data && typeof (data as any).message === 'string' && (data as any).message.trim())
          ? (data as any).message.trim()
          : uiText("Processing complete.");
      alert(message);
      await refresh();
      onClose();
    } catch (e: any) {
      console.error('[PlanChangeFlowModal] submit failed', e);
      const msg = typeof e?.message === 'string' ? e.message : '';
      const code = typeof e?.code === 'string' ? e.code : '';
      if (code === 'failed-precondition') {
        setError(msg || uiText("Stripe setup is incomplete."));
        return;
      }
      if (code === 'permission-denied') {
        setError(msg || uiText("This action requires workspace admin permission."));
        return;
      }
      setError(uiText("Failed to start the plan change. Please try again later."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="upgrade-modal-overlay"
      onClick={onClose}
      style={{ zIndex: 100000 }}
    >
      <div
        className="upgrade-modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560 }}
      >
        <div className="upgrade-modal-header">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <h2 style={{ margin: 0, fontSize: 18 }}>{uiText("Change plan")}</h2>
            <div style={{ fontSize: 12, color: '#666', fontWeight: 600 }}>
              {uiText("Target:")} <span style={{ color: '#333' }}>{workspaceInfo?.name || workspaceId}</span>
            </div>
            <div style={{ fontSize: 12, color: '#666', fontWeight: 600 }}>
              {uiText("Current:")} <span style={{ color: '#333' }}>{currentPlanId}</span> {uiText("→ Change to:")} <span style={{ color: '#333' }}>{targetPlanId}</span>
            </div>
          </div>
          <button className="upgrade-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="upgrade-modal-body" style={{ paddingTop: 12 }}>
          {loading ? (
            <div style={{ fontSize: 13, color: '#666' }}>{uiText("Loading now...")}</div>
          ) : error ? (
            <div style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(211,47,47,0.35)', background: 'rgba(211,47,47,0.06)', color: '#2b2b2b', fontSize: 13, lineHeight: 1.5 }}>
              {error}
            </div>
          ) : null}

          {isChildWorkspace && !loading && (
            <div style={{ marginTop: 12 }}>
              <div style={{ padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(43,43,43,0.12)', background: 'rgba(255,255,255,0.70)', color: '#2b2b2b', fontSize: 13, lineHeight: 1.5 }}>
                {uiText("This child workspace cannot change its plan on its own.")}
                <br />
                {uiText("Parent workspace (")}{workspaceInfo?.parentWorkspaceId}{uiText("). Please change its plan.")}
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
                <button
                  type="button"
                  className="plan-button upgrade"
                  onClick={() => {
                    const parentId = workspaceInfo?.parentWorkspaceId;
                    if (!parentId) return;
                    openInNewTab(`/${parentId}`);
                  }}
                >
                  {uiText("Open parent workspace")}
                </button>
                <button
                  type="button"
                  className="plan-button upgrade"
                  onClick={() => {
                    const parentId = workspaceInfo?.parentWorkspaceId;
                    if (!parentId) return;
                    window.location.assign(`/${parentId}`);
                  }}
                >
                  {uiText("Switch to parent")}
                </button>
              </div>
            </div>
          )}

          {!isChildWorkspace && !loading && (
            <>
              {targetPlanId === 'free' ? (
                <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(43,43,43,0.12)', background: 'rgba(255,255,255,0.70)', color: '#2b2b2b', fontSize: 13, lineHeight: 1.5 }}>
                  {uiText("Changing to free cancels the paid plan subscription at the end of the period (applied at period end, no proration).")}
                </div>
              ) : (
                <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(43,43,43,0.12)', background: 'rgba(255,255,255,0.70)', color: '#2b2b2b', fontSize: 13, lineHeight: 1.5 }}>
                  {uiText("You will continue to Stripe Checkout to start this paid plan.")}
                </div>
              )}

              {currentPlanId === targetPlanId && (
                <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
                  {uiText("You are already on this plan.")}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="plan-button upgrade" onClick={onClose} disabled={submitting}>
                  {uiText("Cancel")}
                </button>
                <button
                  type="button"
                  className="plan-button upgrade"
                  onClick={submit}
                  disabled={!canProceed}
                >
                  {submitting ? uiText("Processing...") : (targetPlanId === 'free' ? uiText("Schedule cancellation") : uiText("Start payment"))}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
